import { useApiClient } from './api';
import { environment } from '../config/environment';

export interface AIAnalysisResponse {
    success: boolean;
    analysis?: string;
    metadata?: {
        assetSymbol: string;
        transactionId: string | number;
        hasBalanceMismatch: boolean;
        transactionCount: number;
    };
    analysisSummary?: {
        transactionCount: number;
        timeframe: string;
        focusTransaction: string;
        asset: string;
    };
    error?: string;
    details?: string;
}

export const useAIService = () => {
    const apiClient = useApiClient();

    const analyzeTransactionIssue = async (
        transactionId: string | number,
        assetSymbol: string,
        issueType: string,
        issueData?: any,
        language?: string,
        progressCallback?: (metadata: { transactionCount?: number; dateRange?: string }) => void
    ): Promise<AIAnalysisResponse> => {
        try {
            console.log(`[AI Service] Sending analysis request for transaction ${transactionId}, asset ${assetSymbol}`);

            // Send minimal data to backend - it will fetch everything internally
            const response = await apiClient.post<AIAnalysisResponse>(
                `${environment.expressApiUrl}/api/ai/analyze-transaction-issue`,
                {
                    transactionId,
                    assetSymbol,
                    issueType,
                    issueData,
                    language: language || 'de',
                }
            );

            // Add analysis summary metadata to the response
            const responseWithMetadata = {
                ...response.data,
                analysisSummary: response.data.analysisSummary || {
                    transactionCount: response.data.metadata?.transactionCount || 0,
                    timeframe: response.data.metadata?.dateRange || 'All time',
                    focusTransaction: `#${transactionId}`,
                    asset: assetSymbol
                }
            };

            // Call progress callback if provided
            if (progressCallback && responseWithMetadata.analysisSummary) {
                progressCallback({
                    transactionCount: responseWithMetadata.analysisSummary.transactionCount,
                    dateRange: responseWithMetadata.analysisSummary.timeframe
                });
            }

            return responseWithMetadata;
        } catch (error: any) {
            console.error('AI Service Error:', error);

            // Handle different error scenarios
            if (error.response?.data) {
                return {
                    success: false,
                    error: error.response.data.error || 'Analysis failed',
                    details: error.response.data.details,
                };
            }

            return {
                success: false,
                error: error.message || 'Failed to analyze transaction issue',
            };
        }
    };

    const checkAIServiceHealth = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
        try {
            const response = await apiClient.get<{ success: boolean; message: string; timestamp: string }>(
                `${environment.expressApiUrl}/api/ai/health`
            );

            return {
                success: response.data.success,
                message: response.data.message,
            };
        } catch (error: any) {
            console.error('AI Health Check Error:', error);
            return {
                success: false,
                error: error.message || 'AI service health check failed',
            };
        }
    };

    return {
        analyzeTransactionIssue,
        checkAIServiceHealth,
    };
};